import React, { useEffect } from "react";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { schApi } from "@/lib/schApi";
import { ErrorBox, Loading, useLoad } from "@/components/nds/windows/finance/parts";
import EventForm from "@/components/nds/windows/sch/EventForm";
import EventDetail from "@/components/nds/windows/sch/EventDetail";
import TaskForm from "@/components/nds/windows/sch/TaskForm";

/** Scheduler pop-ups as their own windows. Saves broadcast "sch:changed" so any open calendar / resident widget reloads. */
export const schChanged = () => {
  window.dispatchEvent(new CustomEvent("sch:changed"));
  try { localStorage.setItem("sch:changed", String(Date.now())); } catch { /* other tabs just won't auto-refresh */ }
};

export function useSchChanged(cb) {
  useEffect(() => {
    const onStorage = (e) => e.key === "sch:changed" && cb();
    window.addEventListener("sch:changed", cb);
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener("sch:changed", cb); window.removeEventListener("storage", onStorage); };
  }, [cb]);
}

// In its own browser tab (/admin/w/…) there is no window to close — close the tab instead, or go back to the board.
const finish = (win, closeWindow) => {
  if (win.id !== "tab-main") return closeWindow(win.id);
  window.close();
  setTimeout(() => { if (!window.closed) window.location.assign("/admin"); }, 300);
};

function withMeta(Inner) {
  return function SchWindow({ win }) {
    const [meta, reload] = useLoad(() => schApi.get("/meta"), []);
    if (meta.loading && !meta.data) return <Loading testid="sch-window-loading" />;
    if (meta.error) return <ErrorBox testid="sch-window-error" error={meta.error} onRetry={reload} />;
    return <Inner win={win} meta={meta.data} />;
  };
}

export const openEventForm = (openWindow, { rec, startAt, residentIds } = {}) =>
  openWindow("sch_event_form", { title: rec ? `Edit · ${rec.title}` : "New event", rec, residentIds, startAt: startAt ? new Date(startAt).toISOString() : null });

export const openEventDetail = (openWindow, ev) => openWindow("sch_event_detail", { title: ev.title || "Event", id: ev.id });

export const openTaskForm = (openWindow, { task, date } = {}) => openWindow("sch_task_form", { title: task ? `Edit task · ${task.title}` : "New task", task, date });

export const SchEventFormWindow = withMeta(({ win, meta }) => {
  const { closeWindow } = useFloatingWindows();
  const { rec, startAt, residentIds } = win.payload || {};
  return <EventForm meta={meta} rec={rec} residentIds={residentIds} startAt={startAt ? new Date(startAt) : null} onClose={() => finish(win, closeWindow)}
    onSaved={() => { schChanged(); finish(win, closeWindow); }} />;
});

export const SchEventDetailWindow = withMeta(({ win, meta }) => {
  const { closeWindow, openWindow } = useFloatingWindows();
  return (
    <div className="flex h-full [&>aside]:!w-full [&>aside]:!border-l-0">
      <EventDetail id={win.payload?.id} meta={meta} embedded onClose={() => finish(win, closeWindow)} onChanged={schChanged}
        onEdit={(e) => { closeWindow(win.id); openEventForm(openWindow, { rec: e }); }} />
    </div>
  );
});

export const SchTaskFormWindow = withMeta(({ win, meta }) => {
  const { closeWindow } = useFloatingWindows();
  const { task, date } = win.payload || {};
  return <TaskForm meta={meta} task={task} date={date} onClose={() => finish(win, closeWindow)} onSaved={() => { schChanged(); finish(win, closeWindow); }} />;
});
